import { useCallback, useEffect, useRef } from 'react';
import styles from './OTPInput.module.css';

interface OTPInputProps {
  length?: number;
  value: string;
  onChange: (next: string) => void;
  autoFocus?: boolean;
  ariaLabel?: string;
}

export function OTPInput({
  length = 6,
  value,
  onChange,
  autoFocus = true,
  ariaLabel,
}: OTPInputProps): JSX.Element {
  const refs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
  }, [autoFocus]);

  const setCharAt = useCallback(
    (idx: number, char: string) => {
      const padded = value.padEnd(length, ' ').split('');
      padded[idx] = char || ' ';
      const next = padded.join('').replace(/\s/g, '').slice(0, length);
      onChange(next);
    },
    [length, onChange, value],
  );

  return (
    <div className={styles.row} role="group" aria-label={ariaLabel ?? 'One-time code'}>
      {Array.from({ length }, (_, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          className={styles.cell}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          maxLength={1}
          pattern="[0-9]*"
          value={value[i] ?? ''}
          onChange={(e) => {
            const v = e.target.value.replace(/\D/g, '').slice(-1);
            setCharAt(i, v);
            if (v && i < length - 1) refs.current[i + 1]?.focus();
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace' && !value[i] && i > 0) {
              refs.current[i - 1]?.focus();
            }
            if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
            if (e.key === 'ArrowRight' && i < length - 1) refs.current[i + 1]?.focus();
          }}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);
            if (text) {
              e.preventDefault();
              onChange(text);
              const last = Math.min(text.length, length - 1);
              refs.current[last]?.focus();
            }
          }}
        />
      ))}
    </div>
  );
}
